import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback, useMemo } from "react";

import { EmptystatePickaxeColored } from "components/icons/generated";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import Select from "ds/components/Select";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateWorkerPool from "shared/WorkerPool/access/useCanCreateWorkerPool";
import { ScanFormFields } from "shared/Scan/types";
import { getDocsUrl } from "utils/getDocsUrl";
import { showWorkerPoolFormDrawer } from "views/Account/WorkerPools/CreateWorkerPool";

import { SCHEDULE_SCAN_WIZARD_STEP_HEADERS } from "../constants";
import NewScanScheduleRequirementEmptyState from "../components/RequirementEmptyState";
import { NewScanScheduleContext } from "../context";
import { getMissingScanRequirements } from "../helpers";
import { NewScanScheduleStep } from "../types";

const NewScanScheduleStepAttachWorkerPool = () => {
  const { control } = useFormContext<ScanFormFields>();

  const {
    goToStep,
    formOptions,
    formOptions: { workerPools, refetch: refetchFormOptions },
  } = useTypedContext(NewScanScheduleContext);

  const space = useWatch({ control, name: "space" });
  const canCreateWorkerPool = useCanCreateWorkerPool(space);

  const { hasNoAttachableWorkerPool } = getMissingScanRequirements(formOptions, space);

  const handleCreateWorkerPool = useCallback(() => {
    showWorkerPoolFormDrawer({
      initialSpaceId: space,
      onSuccess: () => refetchFormOptions(),
    });
  }, [space, refetchFormOptions]);

  const handleChangeSpace = useCallback(() => {
    goToStep(NewScanScheduleStep.ScanDetails);
  }, [goToStep]);

  const workerPoolOptions = useMemo(
    () => workerPools.map(({ id, name }) => ({ value: id, label: name })),
    [workerPools]
  );

  return (
    <FullScreenModalBody>
      <FullScreenModalBodyHeader
        {...SCHEDULE_SCAN_WIZARD_STEP_HEADERS[NewScanScheduleStep.AttachWorkerPool]}
      />
      <Box direction="column">
        {hasNoAttachableWorkerPool && (
          <NewScanScheduleRequirementEmptyState
            icon={EmptystatePickaxeColored}
            title="No private worker pools in selected space"
            caption="Scans run on a private worker pool, which also lets Spacelift reach resources in your private network. To continue, create a worker pool in this space or go back and choose a different space."
            primaryActionLabel="Create"
            primaryAction={canCreateWorkerPool}
            onPrimaryAction={handleCreateWorkerPool}
            onChangeSpace={handleChangeSpace}
            documentationUrl={getDocsUrl("/concepts/worker-pools")}
          />
        )}

        {!hasNoAttachableWorkerPool && (
          <Controller
            name="workerPoolId"
            control={control}
            rules={{ required: "Worker pool is required." }}
            render={({ field, fieldState }) => (
              <FormField label="Worker pool" error={fieldState.error?.message}>
                {({ ariaInputProps }) => (
                  <Select
                    value={field.value}
                    items={workerPoolOptions}
                    onChange={field.onChange}
                    placeholder="Please select"
                    isError={!!fieldState.error}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            )}
          />
        )}
      </Box>
    </FullScreenModalBody>
  );
};

export default NewScanScheduleStepAttachWorkerPool;
